// Écran d'accueil après connexion : hub vers tous les modules, en identité BD-SapoPro (BDSP).
// Conserve l'accès à l'intégralité des modules (aucune régression de navigation) ; seul
// l'habillage change (logo, en-tête, tuiles). Les couleurs bda* restent sur les autres écrans.
import { useState } from 'react'
import { moduleOuvert } from '@shared/constants'
import { useAuth } from '@/lib/auth'
import BandeauFinEssai from '@/components/BandeauFinEssai'
import { useRaccourcis } from '@/lib/raccourcis'
import TableauBord from '@/pages/TableauBord'

interface Props {
  onOuvrirCalculateur: () => void
  onOuvrirIngredients: () => void
  onOuvrirFormules: () => void
  onOuvrirArticles: () => void
  onOuvrirStock: () => void
  onOuvrirClients: () => void
  onOuvrirDevis: () => void
  onOuvrirFactures: () => void
  onOuvrirDepots: () => void
  onOuvrirComptabilite: () => void
  onOuvrirFiches: () => void
  onOuvrirReglages: () => void
  // Entrées cosmétiques (affichées seulement si le module « cosmétique » est actif).
  onOuvrirFormulateur: () => void
  onOuvrirFichesCosmetique: () => void
}

// Familles de modules -> habillage de la pastille (couleur = catégorie, pas décoration).
type Famille = 'prod' | 'stock' | 'vente' | 'sys'
const PASTILLE: Record<Famille, string> = {
  prod: 'bg-bdspIndigoSoft text-bdspIndigo',
  stock: 'bg-bdspSlateSoft text-bdspSlate',
  vente: 'bg-emerald-50 text-emerald-700',
  sys: 'bg-stone-100 text-stone-600',
}

export default function Accueil(props: Props) {
  const { user, logout } = useAuth()
  const { urlAgenda, ouvrirAgenda, ouvrirCanva, ouvrirDistriDoc, ouvrirEspaceDocPro } = useRaccourcis()
  const [chargesOuvert, setChargesOuvert] = useState(true)

  // Tuiles dans l'ordre des familles (Production -> Stock -> Ventes -> Système).
  // `module` optionnel : la tuile n'apparaît que si l'abonnement au module est actif (ex.
  // cosmétique). Les entrées cosmétiques sont donc masquées tant que le module n'est pas payé.
  const tuiles: {
    emoji: string
    label: string
    desc: string
    famille: Famille
    onClick: () => void
    module?: 'cosmetique'
    // Tuile de DÉCOUVERTE : affichée SEULEMENT si le module n'est PAS détenu (upsell vers l'abonnement).
    moduleAbsent?: 'cosmetique'
  }[] = [
    { emoji: '🧼', label: 'Calculateur', desc: 'Formuler un savon', famille: 'prod', onClick: props.onOuvrirCalculateur },
    { emoji: '📒', label: 'Mes formules', desc: 'Recettes enregistrées', famille: 'prod', onClick: props.onOuvrirFormules },
    { emoji: '📋', label: 'Lots de production', desc: 'Fabrications et cures', famille: 'prod', onClick: props.onOuvrirFiches },
    { emoji: '⚗️', label: 'Formulateur cosmétique', desc: 'Formuler un cosmétique', famille: 'prod', onClick: props.onOuvrirFormulateur, module: 'cosmetique' },
    { emoji: '⚗️', label: 'Lots cosmétiques', desc: 'Fabrications cosmétiques', famille: 'prod', onClick: props.onOuvrirFichesCosmetique, module: 'cosmetique' },
    { emoji: '🧪', label: 'Mes ingrédients', desc: 'Matières premières', famille: 'stock', onClick: props.onOuvrirIngredients },
    { emoji: '🏷️', label: 'Mes articles', desc: 'Catalogue produits', famille: 'stock', onClick: props.onOuvrirArticles },
    { emoji: '📦', label: 'Stock', desc: 'Entrées et sorties', famille: 'stock', onClick: props.onOuvrirStock },
    { emoji: '👤', label: 'Mes clients', desc: 'Base clients', famille: 'vente', onClick: props.onOuvrirClients },
    { emoji: '🧾', label: 'Mes devis', desc: 'Propositions commerciales', famille: 'vente', onClick: props.onOuvrirDevis },
    { emoji: '📑', label: 'Mes factures', desc: 'Facturation et Factur-X', famille: 'vente', onClick: props.onOuvrirFactures },
    { emoji: '🏪', label: 'Dépôts-vente', desc: 'Dépôts et reversements', famille: 'vente', onClick: props.onOuvrirDepots },
    { emoji: '💶', label: 'Recettes & achats', desc: 'Livre des recettes, achats et frais', famille: 'vente', onClick: props.onOuvrirComptabilite },
    { emoji: '⚙️', label: 'Réglages', desc: 'Paramètres du compte', famille: 'sys', onClick: props.onOuvrirReglages },
    // Lien SORTANT vers l'outil gratuit BD-DistriDoc (nouvel onglet). Mise en avant volontaire au
    // lancement : le même lien figure aussi dans la rangée de raccourcis ci-dessous et dans le
    // menu ☰. Redondance ASSUMÉE (décision du 20/07/2026), à ne pas « corriger » d'initiative.
    // Le sous-titre dit ce que l'outil NE fait PAS : l'application est vendue à des tiers, et
    // laisser croire qu'un générateur de documents dispense d'un évaluateur les mettrait en défaut.
    { emoji: '📄', label: 'Générateur de documents', desc: 'Mise en forme seule · ne remplace pas l’évaluateur ↗', famille: 'sys', onClick: ouvrirDistriDoc },
    // Lien SORTANT vers l'espace client BD-DocPro (nouvel onglet, URL fixe). Point d'entrée permanent
    // pour suivre ses dossiers DIP/RSPC déjà déposés. Le même lien figure dans le menu ☰ ; lien
    // statique sans couplage (cf. raccourcis.ts).
    { emoji: '🗂️', label: 'Mon espace BD-DocPro', desc: 'Suivre mes dossiers DIP/RSPC ↗', famille: 'sys', onClick: ouvrirEspaceDocPro },
  ]

  // Filtre de gating : une tuile `module` n'apparaît que si ce module est actif ; une tuile
  // `moduleAbsent` (découverte) n'apparaît QUE si le module n'est PAS détenu.
  // Un module SUSPENDU (shared/constants.ts) disparaît dans les DEUX cas : ni accès, ni upsell.
  const modules = user?.modules ?? []
  const tuilesVisibles = tuiles.filter((t) => {
    if (t.module) return moduleOuvert(modules, t.module)
    if (t.moduleAbsent) return !moduleOuvert(modules, t.moduleAbsent)
    return true
  })

  const initiale = user?.email?.[0]?.toUpperCase() ?? 'B'

  return (
    <div className="min-h-screen bg-bdspBg p-5 sm:p-8">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-7 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/logo-bdsp.png" alt="BD-SapoPro" className="h-12 w-auto sm:h-14" />
            <div>
              <h1 className="text-2xl font-bold text-bdspInk">Bonjour 👋</h1>
              <p className="text-sm text-bdspSlate">Que veux-tu faire aujourd'hui ?</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              title={user?.email}
              className="hidden h-10 w-10 items-center justify-center rounded-full bg-bdspIndigo text-sm font-semibold text-white sm:flex"
            >
              {initiale}
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium text-bdspInk transition hover:bg-white"
            >
              Se déconnecter
            </button>
          </div>
        </header>

        <BandeauFinEssai onVoirFormules={props.onOuvrirReglages} />

        <div className="mb-5 flex flex-wrap gap-2">
          {urlAgenda && urlAgenda.trim() ? (
            <button
              type="button"
              onClick={ouvrirAgenda}
              className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-bdspInk shadow-sm transition hover:bg-stone-50"
            >
              <span aria-hidden="true">📅</span> Mon agenda ↗
            </button>
          ) : null}
          <button
            type="button"
            onClick={ouvrirCanva}
            className="inline-flex items-center gap-2 rounded-lg border border-bdspIndigo bg-bdspIndigoSoft px-3 py-2 text-sm font-medium text-bdspInk shadow-sm transition hover:opacity-90"
          >
            <span aria-hidden="true">🏷️</span> Étiquette Canva ↗
          </button>
          <button
            type="button"
            onClick={ouvrirDistriDoc}
            className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-bdspInk shadow-sm transition hover:bg-stone-50"
          >
            <span aria-hidden="true">📄</span> Générateur de documents ↗
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {tuilesVisibles.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={t.onClick}
              className="group flex items-start gap-3 rounded-2xl border border-stone-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-bdspIndigo/40 hover:shadow-md"
            >
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl ${PASTILLE[t.famille]}`}>
                {t.emoji}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-bdspInk">{t.label}</span>
                <span className="mt-0.5 block text-xs text-bdspSlate">{t.desc}</span>
              </span>
            </button>
          ))}
        </div>

        <section className="mt-6">
          <button
            type="button"
            onClick={() => setChargesOuvert((o) => !o)}
            className="flex w-full items-center justify-between rounded-2xl border border-stone-200 bg-white px-4 py-3 text-left text-sm font-semibold text-bdspInk shadow-sm transition hover:border-bdspIndigo/40"
            aria-expanded={chargesOuvert}
          >
            <span>📊 Charges et déclarations (URSSAF)</span>
            <span className="text-bdspSlate">{chargesOuvert ? '▲' : '▼'}</span>
          </button>
          {chargesOuvert && (
            <div className="mt-3">
              <TableauBord integre />
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
